import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysUser } from './queryKeys';
import { type UserCompany } from './useQueryUserCompany';

/**
 * Fetches company data for a current user from the API.
 * @returns  The company data if successful, undefined otherwise
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/get_company_user_company_get
 */
export const fetchCurrentUserCompany = async () => {
  const response = await vestigasApi
    .get(ENDPOINT.USER.GET_COMPANY_FOR_CURRENT_USER())
    .json<UserCompany>();

  return response;
};

/**
 * React Query based custom hook for getting the company data for a current user.
 * @param userId - The ID of the user to fetch company data for
 * @param options - Additional options for the useQuery hook
 * @returns The result of the useQuery hook
 */
export const useQueryCurrentUserCompany = <TSelected = UserCompany>(
  options?: Omit<
    UseQueryOptions<UserCompany, Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<UserCompany, Error, TSelected>({
    queryFn: async () => fetchCurrentUserCompany(),
    queryKey: queryKeysUser.getCompanyForCurrentUser(),
    staleTime: ms('10m'),
    ...options,
  });
